/**
 * Uniquely identifying a material variant based on persistent ids.
 * @module
 */
import type { MasonryId } from "./masonryTypes";

export interface MaterialSrc {
  templateId: number;
  layerConfigId?: number;
  colorId?: number;
  keepPreviousLayers?: boolean;
  userDefinedName?: string;
  userDefinedCategory?: string;
}

/**
 * @param src MaterialSrc identifying the material
 * @returns A persistent cache key. Can be used to fetch cached files like material thumbnails, so the values are promised to be stable over time.
 */
export function materialCacheKey(src: MaterialSrc): string {
  const key = [src.templateId, src.layerConfigId, src.colorId];
  return key.join("_"); // URL-friendly delimiter
}

/**
 * Identifies a material in user-facing data while preserving distinct user-defined names.
 * Unnamed materials retain their existing cache key for backwards compatibility.
 */
export function materialEntityKey(src: MaterialSrc): string {
  const cacheKey = materialCacheKey(src);
  return src.userDefinedName ? `${cacheKey}:${encodeURIComponent(src.userDefinedName)}` : cacheKey;
}

export function matchesMaterialCacheKeyFormat(key: string): boolean {
  return key.match(/^\d+_\d*_\d*$/) !== null;
}

/**
 * Dedupes material sources by their cache key, keeping the last occurrence per key.
 * Returns a `Map<cacheKey, MaterialSrc>`; callers that want just the unique sources use `.values()`.
 */
export function materialSourcesByKey(sources: readonly MaterialSrc[]): Map<string, MaterialSrc> {
  const byKey = new Map<string, MaterialSrc>();
  for (const src of sources) {
    byKey.set(materialCacheKey(src), src);
  }
  return byKey;
}

export function materialSrcFromCacheKey(key: string): MaterialSrc {
  const [templateId, layerConfigId, colorId] = key.split("_");

  const materialSrc: MaterialSrc = { templateId: parseInt(templateId) };
  if (layerConfigId !== "") {
    materialSrc.layerConfigId = parseInt(layerConfigId);
  }
  if (colorId !== "") {
    materialSrc.colorId = parseInt(colorId);
  }

  return materialSrc;
}

export function materialSrcEquals(a: MaterialSrc | null | undefined, b: MaterialSrc | null | undefined): boolean {
  if (a === undefined || a === null || b === undefined || b === null) {
    return (a === undefined || a === null) === (b === undefined || b === null);
  }
  if (a.templateId !== b.templateId) {
    return false;
  }
  if (a.layerConfigId !== b.layerConfigId) {
    return false;
  }
  if (a.colorId !== b.colorId) {
    return false;
  }
  return true;
}

export function isMasonryId(matId: MaterialSrc | MasonryId | undefined): matId is MasonryId {
  return typeof matId === "number";
}

/**
 * Only tests that the material is defined.
 * The combination of templateId, layerConfigId, and colorId might not exist in the material library.
 */
export function isDesignMaterialSrc(
  materialSrc: MaterialSrc | MasonryId | undefined | null,
): materialSrc is MaterialSrc {
  return materialSrc !== undefined && materialSrc !== null && (materialSrc as MaterialSrc).templateId !== undefined;
}
